import { Controller } from '@hotwired/stimulus';
import { detectTransition, pendingRecipes } from './mixins/recipe_transition';

// Drives the unified "Confirm Changes" modal (_plan_change_confirmation_modal).
// On open it populates the New Plan card from the live form and shows the right
// contextual note. The delivery-date field is the shared `datepicker` controller
// (same widget as the Orders change-date flow); its hidden input carries the
// chosen date, so Confirm submits the plan and date together.
export default class extends Controller {
  static targets = [
    'newRecipe',
    'newInterval',
    'newIntervalUnit',
    'newPrice',
    'newFeedingAmount',
    'recipeNote',
    'calorieNote',
  ];

  static values = {
    context: String,
    originalRecipes: Array,
    transitionMappings: Array,
    previewUrl: String,
  };

  connect() {
    // Repopulate each time the modal opens so the New Plan card and recipe note
    // reflect the customer's current selections.
    this.onShow = () => this.prepare();
    this.element.addEventListener('show.bs.modal', this.onShow);
  }

  disconnect() {
    if (this.onShow) this.element.removeEventListener('show.bs.modal', this.onShow);
  }

  prepare() {
    this.#populateNewPlan();
    this.#toggleRecipeNote();
  }

  #populateNewPlan() {
    if (this.contextValue === 'dog') {
      // No plan selector on the dog page — the new plan is whatever the recomputed
      // calories map to, so fetch it from the server using the pending form values.
      this.#previewDogPlan();
      return;
    }

    const recipeInput = document.querySelector('input[name="recipe"]:checked');
    if (recipeInput && this.hasNewRecipeTarget) {
      this.newRecipeTarget.textContent =
        recipeInput.dataset.recipeLabel || recipeInput.value;
    }

    const intervalInput = document.querySelector(
      'input[name="subscription[interval_count]"]:checked'
    );
    const days = parseInt(intervalInput?.value, 10);
    if (days && this.hasNewIntervalTarget) {
      this.newIntervalTarget.textContent = days;
      if (this.hasNewIntervalUnitTarget) {
        this.newIntervalUnitTarget.textContent = days === 1 ? 'day' : 'days';
      }
    }

    const priceLabel = intervalInput?.dataset.pricePerDayLabel;
    if (priceLabel && this.hasNewPriceTarget) {
      this.newPriceTarget.textContent = priceLabel;
    }

    // The checked radio carries the feeding label for its recipe and plan type
    // (full/half payloads are separate), so no extra plan-type lookup is needed.
    const feedingLabel = intervalInput?.dataset.feedingLabel;
    if (feedingLabel && this.hasNewFeedingAmountTarget) {
      this.newFeedingAmountTarget.textContent = feedingLabel;
    }
  }

  /**
   * Fetches the recomputed plan for the pending dog attributes and fills the New
   * Plan card. On any failure the server-rendered current plan is left in place.
   * @private
   */
  #previewDogPlan() {
    if (!this.hasPreviewUrlValue || !this.previewUrlValue) return;
    const form = this.element.closest('form');
    if (!form) return;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new FormData(form);
    body.delete('_method'); // keep this a POST — Rack::MethodOverride would turn the form's _method=patch into a PATCH
    fetch(this.previewUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': token, Accept: 'application/json' },
      body,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => this.applyPreview(data))
      .catch(() => {});
  }

  /**
   * Fills the New Plan card from a plan preview payload. Public so the admin dog
   * form can fill the card from its own preview before opening the modal.
   * @param {?Object} data plan preview JSON; ignored when it has no interval_count
   */
  applyPreview(data) {
    if (!data || !data.interval_count) return;
    if (this.hasNewIntervalTarget) this.newIntervalTarget.textContent = data.interval_count;
    if (this.hasNewIntervalUnitTarget) this.newIntervalUnitTarget.textContent = data.interval_unit;
    if (this.hasNewPriceTarget && data.price_per_day_label) {
      this.newPriceTarget.textContent = data.price_per_day_label;
    }
    if (this.hasNewFeedingAmountTarget && data.feeding_amount_label) {
      this.newFeedingAmountTarget.textContent = data.feeding_amount_label;
    }
  }

  /**
   * Shows the slow-transition note only for RecipeTransition pairs (beef↔fish,
   * beef_small_bites↔fish). Recipe only changes on Edit Plan, so the note never
   * applies in the dog (calorie) context.
   * @private
   */
  #toggleRecipeNote() {
    if (!this.hasRecipeNoteTarget) return;
    if (this.contextValue !== 'plan') {
      this.recipeNoteTarget.hidden = true;
      return;
    }
    const isTransition = detectTransition(
      this.originalRecipesValue,
      this.transitionMappingsValue,
      pendingRecipes()
    );
    this.recipeNoteTarget.hidden = !isTransition;
  }
}
